export class WeCollectionScrollPosition {
  constructor () {
    this.storageKey = 'weCollectionScrollPosition'
    this.currentUrl = this.urlToPathName(window.location.href)
    this.isCollectionPage = this.currentUrl.includes('/collections/')
    this.isProductPage = this.currentUrl.includes('/products/')

    // Run init immediately
    this.init()
  }

  init () {
    if (this.isCollectionPage) {
      this.handleCollectionPage()
    }
    // We don't need logic on the product page, the click listener on the collection page handles the saving
  }

  handleCollectionPage () {
    // 1. Disable browser's native scrollRestoration immediately
    // This is crucial for mobile to prevent "fighting" between your script and the browser
    if ('scrollRestoration' in history) {
      history.scrollRestoration = 'manual'
    }

    // 2. Initial check on load (for full page loads/reloads)
    this.checkAndRestoreScrollPosition()

    // 3. Listen for Back/Forward navigation (Desktop)
    this.setupPopstateListener()

    // 4. Listen for Page Show (Mobile Back Button / Cache)
    this.setupPageshowListener()

    // 5. Setup Click Listeners
    this.captureProductClicks()
  }

  checkAndRestoreScrollPosition () {
    const savedData = this.getSavedData()
    
    // Safety check: only restore if we are on the same collection URL where we clicked
    if (savedData && savedData.collectionUrl === this.currentUrl) {

      // We rely on retries, so we trigger this immediately wrapped in animation frame
      requestAnimationFrame(() => {
        // Mobile needs a slight delay to allow layout to settle
        setTimeout(() => {
          this.restoreScrollPosition(savedData.productHandle)
        }, 100)
      })
    }
  }

  setupPopstateListener () {
    window.addEventListener('popstate', () => {
      // Re-verify we are on a collection page
      const currentPath = this.urlToPathName(window.location.href)
      
      if (currentPath.includes('/collections/')) {
        this.currentUrl = currentPath
        this.checkAndRestoreScrollPosition()
      }
    })
  }

  setupPageshowListener () {
    window.addEventListener('pageshow', (event) => {
      // FIX FOR MOBILE:
      // We removed "if (event.persisted)"
      // Reason: On mobile, if memory is low, the browser reloads the page (persisted = false).
      // We still want to scroll back to the product in that case.
      
      const currentPath = this.urlToPathName(window.location.href)
      
      if (currentPath.includes('/collections/')) {
        this.currentUrl = currentPath
        
        // If persisted (bfcache), the page is already there, but we need to re-enforce scroll
        // If NOT persisted (reload), the DOM is loading, so the retries in restoreScrollPosition will handle it
        setTimeout(() => {
            this.checkAndRestoreScrollPosition()
        }, 150)
      }
    })
  }

  captureProductClicks () {
    document.addEventListener('click', (e) => {
        const currentPath = this.urlToPathName(window.location.href)
        if (!currentPath.includes('/collections/')) return

        // Find the product card
        const productCard = e.target.closest('[data-product-handle]')
        if (!productCard) return

        const productHandle = productCard.getAttribute('data-product-handle')
        if (!productHandle) return

        // Save data
        this.saveData({
          collectionUrl: currentPath,
          productHandle: productHandle,
          timestamp: Date.now(),
        })
      }, true // Capture phase
    )
  }

  restoreScrollPosition (productHandle) {
    if (!productHandle) return

    let attempts = 0
    const maxAttempts = 15 // Increased attempts for slower mobile connections
    const attemptDelay = 150 

    const checkForProduct = () => {
      attempts++
      
      const productCard = document.querySelector(`[data-product-handle="${productHandle}"]`)

      if (productCard) {
        // Product Found!
        requestAnimationFrame(() => {
          productCard.scrollIntoView({
            behavior: 'auto', // 'auto' is instant, 'smooth' is bad for restoration
            block: 'center',  // CHANGED: 'center' prevents sticky headers from covering the product
            inline: 'center'
          })
          
          // Clear data so we don't scroll again if the user refreshes manually later
          this.clearData()
        })
      } else if (attempts < maxAttempts) {
        // Not found, retry
        setTimeout(checkForProduct, attemptDelay)
      } else {
        // Stop trying
        this.clearData()
      }
    }

    checkForProduct()
  }

  saveData (data) {
    try { sessionStorage.setItem(this.storageKey, JSON.stringify(data)) } catch (e) {}
  }

  getSavedData () {
    try {
      const data = sessionStorage.getItem(this.storageKey)
      return data ? JSON.parse(data) : null
    } catch (e) { return null }
  }

  clearData () {
    try { sessionStorage.removeItem(this.storageKey) } catch (e) {}
  }

  urlToPathName (url) {
    try {
      const object = new URL(url)
      return object.pathname
    } catch (e) {
      return url.split('?')[0]
    }
  }
}

// Initialize
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => new WeCollectionScrollPosition())
} else {
  new WeCollectionScrollPosition()
}